/**
 * Packaging configs editor for Settings > My-Kitchen (Plan 19).
 *
 * Schema note (per the build packet's "DO NOT modify Prisma schema"
 * rule): the legacy `PackagingConfig` model the plan markdown references
 * has been replaced by the Bundle / BundleDeliverable /
 * BundleDeliverablePackaging trio in the current schema (see the
 * `catalog.prisma` comment on the Bundle model). Per-shipment packaging
 * is authored on the Bundle's deliverable rows, which live in
 * `apps/menu-builder-demo` (catalog authoring), not here.
 *
 * Plan 19's customer-facing surface, then, is read-only context: the
 * chef sees an explanation of where packaging is configured + a CTA to
 * route a change request through the standard Plan-27 workflow. The
 * editor UI itself is **deferred to apps/menu-builder-demo** per §1a of the
 * build packet.
 */

import { Badge } from '@ordo/ui/badge'
import { Button } from '@ordo/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@ordo/ui/card'
import { Box, Pencil } from 'lucide-react'
import { useState } from 'react'

import { KitchenChangeRequestModal } from './kitchen-change-request-modal'

export interface PackagingConfigsEditorProps {
  kitchenOrgId: string
}

export function PackagingConfigsEditor({ kitchenOrgId }: PackagingConfigsEditorProps) {
  const [requestOpen, setRequestOpen] = useState(false)
  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Box className="h-4 w-4" />
            Packaging configs
            <Badge variant="outline" className="ml-2 text-xs">
              Authored in menu-builder
            </Badge>
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Per-shipment packaging — pans, lids, cold packs, serving counts — is now defined on the Bundle's deliverable
            rows in the Ordo catalog. Use the "Request a change" CTA to suggest a new packaging pattern or a tweak to an
            existing one; the Ordo catalog team applies the change via the menu-builder authoring app.
          </p>
        </CardHeader>
        <CardContent>
          <Button type="button" variant="outline" size="sm" onClick={() => setRequestOpen(true)}>
            <Pencil className="mr-1 h-3 w-3" />
            Request a packaging change
          </Button>
        </CardContent>
      </Card>

      {requestOpen ? (
        <KitchenChangeRequestModal
          open
          onOpenChange={setRequestOpen}
          kitchenOrgId={kitchenOrgId}
          field="packagingConfig"
          fieldLabel="Packaging configuration"
          currentValue={null}
        />
      ) : null}
    </>
  )
}
